import type { WorkspaceContext } from '@template-ui/blocks';
import { domainBlockManifests } from './domain-block-manifests';
import { SDMF_FACTORS } from '../hearing-record/sdmf-analysis.models';
export function domainBlockPreview(
  code: string,
  context: WorkspaceContext,
  observedUtc: string,
  version = 1,
): unknown {
  if (!domainBlockManifests.some((manifest) => manifest.code === code))
    throw new Error('No domain preview installed.');
  const occurrenceId = context.workflow!.stepOccurrenceId;
  if (code.startsWith('notices.'))
    return {
      occurrenceId,
      sourceContractVersion: 2,
      noticeGroupId: 'synthetic-notice-group',
      bindingRevision: 1,
      contextRevision: 1,
      contextHash: 'synthetic-notice-source',
      bindingState: 'resolved',
      canInitializeBinding: false,
      referenceRecordTypeCode: 'scheduled-proceeding',
      incarceratedPersonId: 1,
      scheduledProceedingId: 1,
      scopeRevision: 1,
      scopeState: 'ready',
      isCurrent: true,
      canAuthor: false,
      eTag: 'synthetic',
      templateVersionId: 1,
      observedUtc,
      snapshot: 'synthetic-notice-preview',
      totalCount: 1,
      page: 1,
      pageSize: 25,
      hasMore: false,
      contactAddress: null,
      title: null,
      body: null,
      notes: null,
      pdfUrl: null,
      associations: [],
      syntheticPreview: true,
      rows: [
        {
          id: 'synthetic-recipient',
          kind: code === 'notices.recipients' ? 'recipient' : 'obligation',
          participantId: 1,
          onBehalfOfParticipantId: null,
          displayName: 'Example recipient',
          onBehalfOfName: null,
          roleCode: 'victim',
          obligationKey: 'synthetic-obligation',
          noticeId: null,
          attemptId: null,
          revision: 1,
          outcome: null,
          occurredUtc: null,
          recordedUtc: null,
          actorUserId: null,
          noticeMilestone: 'sent',
          responseRequired: false,
          isActive: true,
          isRelevant: true,
          isAdopted: false,
          statusCode: 'unresolved',
          templateVersion: null,
          channelCode: null,
        },
      ],
    };
  const base = {
    occurrenceId,
    bindingId: 1,
    bindingRevision: 1,
    observedUtc,
    snapshot: 'synthetic-domain-preview-v1',
    availability: 'present',
    factPosture: 'current-observed',
    totalCount: 1,
    page: 1,
    pageSize: 25,
    hasMore: false,
    syntheticPreview: true,
    ...(version >= 2 ||
    [
      'inmate.offenses',
      'inmate.incarceration-history',
      'hearings.history',
    ].includes(code)
      ? {
          sourceContractVersion: 2,
          referenceRecordTypeCode: 'scheduled-proceeding',
        }
      : {}),
  };
  if (code === 'inmate.offenses')
    return {
      ...base,
      rows: [
        {
          id: 1,
          eventDate: '2010-03-01',
          ...(version >= 2
            ? { recordCode: 'BUR-RES', recordLabel: 'Residential burglary' }
            : { summary: 'Synthetic recorded offense.' }),
          caseNumber: 'EXAMPLE-01',
          countNumber: 1,
          statuteReference: 'Recorded statute',
          dispositionCode: 'recorded',
          dispositionDate: '2010-04-01',
          statusCode: 'recorded',
          sourceSystemCode: 'Example source',
          sourceRecordKey: 'SYNTHETIC-1',
          sourceVersion: 'synthetic-v1',
        },
      ],
    };
  if (code === 'inmate.incarceration-history')
    return {
      ...base,
      rows: [
        {
          id: 1,
          facilityLabel: 'Example recorded facility',
          securityLevel: 'Recorded level',
          eventTypeCode: 'admission',
          movementReason: null,
          startedOn: '2010-04-15',
          endedOn: null,
          isCurrent: true,
          ordinal: 1,
          sourceVersion: 'synthetic-v1',
        },
      ],
    };
  if (code === 'hearings.history')
    return {
      ...base,
      rows: [
        {
          id: 1,
          hearingDate: '2025-06-10',
          hearingType: 'Recorded type',
          decisionCode: 'recorded',
          panelSummary: 'Example panel',
          notes: 'Synthetic history note.',
          sourceVersion: 'synthetic-v1',
        },
      ],
    };
  if (code === 'inmate.summary')
    return {
      ...base,
      rows: [
        {
          personId: 1,
          identifier: 'SYNTHETIC-001',
          displayName: 'Example inmate',
          dateOfBirth: null,
          age: null,
          admissionDate: '2010-04-15',
          eligibilityDate: '2027-04-15',
          projectedReleaseDate: null,
          facilityCode: 'EXAMPLE',
          facilityName: 'Example institution',
          sensitiveIdentityVisible: false,
        },
      ],
    };
  if (code === 'inmate.rvrs' || code === 'inmate.programming')
    return {
      ...base,
      rows: [
        {
          id: 1,
          category: code === 'inmate.rvrs' ? 'violation' : 'programming',
          eventDate: '2026-09-10',
          ...(version >= 4
            ? {
                recordCode: code === 'inmate.rvrs' ? 'RVR-102' : 'HSE',
                recordLabel: code === 'inmate.rvrs' ? 'Unauthorized personal item' : 'High school equivalency preparation',
              }
            : { summary: code === 'inmate.rvrs'
                ? 'Synthetic disciplinary record.'
                : 'Synthetic program participation record.' }),
          severityCode: code === 'inmate.rvrs' ? 'recorded-severity' : null,
          statusCode: 'recorded',
          dispositionCode: null,
        },
      ],
    };
  if (code === 'institution.current-holding')
    return {
      ...base,
      rows: [
        {
          facilityCode: 'EXAMPLE',
          matchState: 'active',
          name: 'Example institution',
          region: 'Example region',
          physicalAddress: '100 Example Road',
          mailingAddress: null,
          hearingModes: 'Video, in person',
          contactName: 'Example coordinator',
          contactPhone: null,
          contactEmail: null,
        },
      ],
    };
  if (code === 'hearings.upcoming')
    return {
      ...base,
      schedulingUrl: null,
      rows: [
        {
          id: 1,
          number: 'SYNTHETIC-H001',
          typeCode: 'suitability',
          statusCode: 'scheduled',
          title: 'Example hearing',
          scheduledStartUtc: '2027-03-15T17:00:00Z',
          scheduledEndUtc: '2027-03-15T18:00:00Z',
          timeZoneId: 'America/Los_Angeles',
          locationName: 'Example hearing venue',
          roomName: null,
          format: 'Video',
          panelSummary: 'Example panel',
          detailUrl: '',
        },
      ],
    };
  return {
    occurrenceId,
    rows: [{ occurrenceId }],
    completionPolicyHash: '0'.repeat(64),
    bindingRevision: 1,
    contextHash: '0'.repeat(64),
    bindingState: 'resolved',
    incarceratedPersonId: 1,
    scheduledProceedingId: 1,
    isCurrent: true,
    featureEnabled: true,
    canAuthor: false,
    canComplete: false,
    canReopen: false,
    canConfirm: false,
    eTag: 'synthetic-preview',
    receiptId: null,
    ...(version === 2
      ? {
          sourceContractVersion: 2,
          referenceRecordTypeCode: 'scheduled-proceeding',
          receiptRevision: null,
          receiptPosture: 'unavailable',
          confirmation: [],
        }
      : {}),
    analysis: [
      {
        id: 1,
        revision: 1,
        statusCode: 'draft',
        isCertified: false,
        summaryNarrative:
          'Synthetic analysis. Complete all rated factors and a summary before certification.',
        completionPolicyCode: null,
        completionPolicyVersion: null,
        completionPolicyHash: null,
        certifiedAtUtc: null,
        certifiedByUserId: null,
      },
    ],
    factors: SDMF_FACTORS.map((factor) => ({
      factorCode: factor.code,
      ratingCode:
        factor.scale === 'standard' || factor.scale === 'standard-na'
          ? 'neutral'
          : factor.scale === 'note-only'
            ? ''
            : 'na',
      note: null,
    })),
    validation: [],
    syntheticPreview: true,
  };
}
